/**
 * @file `ReviewDrawer` — the confirm-before-send review surface every send
 *       routes through, rendered inside the shared {@link Drawer}.
 * @module @labcraft/admin/ui/emails/review-drawer
 *
 * Presentational: the compose surface owns the send mutation + form state and
 * hands this component a review model. Emphasis scales with scope — a single
 * recipient is compact; a broad send (filter-all or count > 25) gets a large
 * amber hero, a warning banner, and a required acknowledge checkbox before Send
 * enables. The Suppressions row is lane-aware (#1619):
 *
 *   - **Marketing** — suppressed addresses are excluded automatically.
 *   - **Transactional** — sent without regard for unsubscription (the only
 *     future gate is a permanent, irrecoverable failure — #1618).
 *
 * On success the same drawer flips to a tally (accepted / suppressed / failed)
 * drawn from the real send response.
 */

import { useEffect, useState, type JSX } from 'react'
import { Link } from 'react-router'
import type { SendBroadcastResponse } from '../../shared/emails_routes.ts'
import { useEmailFrom, useMe, useTestSendMutation } from '../hooks/index.ts'
import { Drawer } from './drawer.tsx'
import { EmailPreview } from './email_preview.tsx'

/**
 * Props for {@link ReviewDrawer}.
 *
 * @property open - Whether the drawer is shown.
 * @property onClose - Return to editing (Back to edit / Esc / backdrop).
 * @property onComposeAnother - Reset the form after a successful send.
 * @property classification - The send-type lane (`marketing` / `transactional`).
 * @property count - Recipient count for the hero + button label.
 * @property scopeLabel - Human recipient scope (e.g. "42 learners").
 * @property whyMatch - Why these recipients match (filter description / picked).
 * @property subject - Composed subject.
 * @property body - Composed body (plaintext, or Markdown source in HTML mode).
 * @property format - `text` or `html`.
 * @property bodyHtml - The rendered HTML preview (HTML mode only).
 * @property broad - Broad-send flag (filter-all or count > 25).
 * @property ack - Broad-send acknowledgement checkbox state.
 * @property onAck - Update the acknowledgement.
 * @property isPending - Whether the send is in flight.
 * @property result - The send response on success, else `null`.
 * @property error - A send error message, or `null`.
 * @property onSend - Fire the send.
 */
export type ReviewDrawerProps = {
  open: boolean
  onClose: () => void
  onComposeAnother: () => void
  classification: 'transactional' | 'marketing'
  count: number
  scopeLabel: string
  whyMatch: string
  subject: string
  body: string
  format: 'text' | 'html'
  bodyHtml: string
  broad: boolean
  ack: boolean
  onAck: (value: boolean) => void
  isPending: boolean
  result: SendBroadcastResponse | null
  error: string | null
  onSend: () => void
}

/** Render the review-before-send drawer. */
export function ReviewDrawer(props: ReviewDrawerProps): JSX.Element {
  const single = props.count === 1
  const marketing = props.classification === 'marketing'
  const sent = props.result !== null

  // Test-send is independent of the main send (no campaign / tracking); its
  // render kind mirrors the lane (transactional → footer-free individual).
  const test = useTestSendMutation()
  const me = useMe()
  const previewFrom = useEmailFrom(props.classification)
  const [testTo, setTestTo] = useState('')
  useEffect(() => {
    const operator = me.data?.admin?.email
    if (operator && testTo === '') setTestTo(operator)
  }, [me.data, testTo])

  const onTestSend = (): void => {
    if (!testTo) return
    test.reset()
    test.mutate({
      to: testTo,
      subject: props.subject,
      body: props.format === 'html' ? props.bodyHtml : props.body,
      format: props.format,
      kind: marketing ? 'campaign' : 'individual',
    })
  }

  const needsAck = props.broad && !props.ack
  const canSend = !props.isPending && !needsAck

  const heroClass = single
    ? 'review-hero__count review-hero__count--single'
    : props.broad
      ? 'review-hero__count review-hero__count--broad'
      : 'review-hero__count'

  const kicker = sent ? 'Sent' : 'Review send'
  const title = sent
    ? single
      ? 'Email sent'
      : 'Broadcast sent'
    : single
      ? 'Send to one learner'
      : `Send to ${props.count}`

  const foot = sent ? (
    <>
      <button
        type="button"
        className="lc-btn lc-btn--ghost"
        onClick={props.onComposeAnother}>
        Compose another
      </button>
      <Link
        className="lc-btn lc-btn--primary"
        to={`/emails/campaigns/${props.result?.campaignId ?? ''}`}>
        View delivery
      </Link>
    </>
  ) : (
    <>
      <button
        type="button"
        className="lc-btn lc-btn--ghost"
        onClick={props.onClose}
        disabled={props.isPending}>
        Back to edit
      </button>
      <div className="review-confirm__send">
        <span className="drawer-foot__hint">
          {single ? 'a focused email' : 'can’t be undone'}
        </span>
        <button
          type="button"
          className="lc-btn lc-btn--primary"
          disabled={!canSend}
          onClick={props.onSend}>
          {props.isPending
            ? 'Sending…'
            : single
              ? 'Send email'
              : `Send to ${props.count}`}
        </button>
      </div>
    </>
  )

  return (
    <Drawer
      open={props.open}
      onClose={
        props.isPending
          ? () => {}
          : sent
            ? props.onComposeAnother
            : props.onClose
      }
      kicker={kicker}
      title={title}
      foot={foot}>
      {sent && props.result ? (
        <div className="sent">
          <span className="sent__badge">✓</span>
          <h2 className="sent__title">
            {single ? 'Email sent' : 'Broadcast sent'}
          </h2>
          <p className="sent__sub">
            {single
              ? `Your email is on its way. Delivery is now tracked.`
              : `${props.whyMatch} · delivery is now being tracked.`}
          </p>
          <div className="sent__tally">
            <div className="tally">
              <span className="tally__n">{props.result.accepted}</span>
              <span className="tally__l">Accepted</span>
            </div>
            {props.result.suppressed > 0 && (
              <div className="tally tally--warn">
                <span className="tally__n">{props.result.suppressed}</span>
                <span className="tally__l">Suppressed</span>
              </div>
            )}
          </div>
          <p className="sent__hint">
            Delivery runs in the background. Watch sent / delivered / failed
            climb on the campaign in the Campaigns tab.
          </p>
        </div>
      ) : (
        <>
          {props.error !== null && (
            <div className="panel-banner panel-banner--error">
              <b>Send failed.</b> {props.error}
            </div>
          )}

          <div className="review-hero">
            <span className="review-hero__k">You’re about to email</span>
            <div className={heroClass}>
              {props.count}
              <span className="u">
                {props.count === 1 ? 'learner' : 'learners'}
              </span>
            </div>
            <div className="review-scopeline">
              <span>
                Scope: <b>{props.scopeLabel}</b> · why they match:{' '}
                {props.whyMatch}
              </span>
            </div>
          </div>

          {props.broad && (
            <div className="panel-banner panel-banner--warn">
              <b>This is a broad send.</b> {props.count} people will receive
              this email. It can’t be unsent.
            </div>
          )}

          <div className="review-rows">
            <div className="review-row">
              <div className="review-row__k">Recipients</div>
              <div className="review-row__v">
                {props.scopeLabel}
                <span className="review-row__note">{props.whyMatch}</span>
              </div>
            </div>
            <div className="review-row">
              <div className="review-row__k">Suppressions</div>
              <div className="review-row__v">
                <span className="review-row__note">
                  {marketing
                    ? 'Unsubscribes, bounces & complaints are excluded automatically.'
                    : 'Transactional — sent without regard for unsubscription.'}
                </span>
              </div>
            </div>
            <div className="review-row">
              <div className="review-row__k">Subject</div>
              <div className="review-row__v review-subject">
                {props.subject}{' '}
                <span className="fmt-tag">
                  {props.format === 'html' ? 'HTML' : 'Text'}
                </span>
              </div>
            </div>
            <div className="review-row">
              <div className="review-row__k">Message</div>
              <div className="review-row__v">
                {props.format === 'html' ? (
                  <EmailPreview
                    from={previewFrom}
                    subject={props.subject}
                    bodyHtml={props.bodyHtml}
                    device="desktop"
                    footer={marketing}
                  />
                ) : (
                  <pre className="review-body">{props.body}</pre>
                )}
              </div>
            </div>
          </div>

          <div className="testsend">
            <div className="testsend__label">Send a test first</div>
            <div className="testsend__row">
              <input
                className="lc-input"
                type="email"
                placeholder="you@labcraft.dev"
                value={testTo}
                onChange={e => setTestTo(e.target.value)}
              />
              <button
                type="button"
                className="lc-btn lc-btn--ghost"
                disabled={test.isPending || testTo === ''}
                onClick={onTestSend}>
                {test.isPending ? 'Sending…' : 'Send test'}
              </button>
            </div>
            {test.data && (
              <div className="testsend__ok">✓ Test sent to {testTo}</div>
            )}
            {test.isError && (
              <div className="testsend__err">
                {test.error.message || 'Test send failed.'}
              </div>
            )}
            <div className="testsend__hint">
              Sends a copy to just this address. Recipients, suppressions, and
              tracking are untouched.
            </div>
          </div>

          {props.broad && (
            <label className="typecheck">
              <span className="typecheck__label">
                <b>Confirm broad send.</b>
              </span>
              <span className="typecheck__ack">
                <input
                  type="checkbox"
                  checked={props.ack}
                  onChange={e => props.onAck(e.target.checked)}
                />
                Yes, send to {props.count} learners
              </span>
            </label>
          )}
        </>
      )}
    </Drawer>
  )
}
